import { FieldRow, AppTextInput } from 'components/FormElements';
import {
  Controller,
  useFormContext,
  useWatch,
  useFieldArray,
} from 'react-hook-form';
import Table from 'components/Table';
import { AppSelectBox } from 'components/FormElements';
import { Delete } from '@mui/icons-material';
import Button from 'components/Button';
import Spacing from 'components/Spacing';
import ButtonGroup from 'components/ButtonGroup';
import { FirmSelector } from 'components/FirmSelector';
import { useGetRole } from 'hooks/ui/useGetRole';
import recalculatePerformanceFee from './helper/recalculate-performance-fee';
import {
  InputAdornment,
  SelectChangeEvent,
  styled,
  Tooltip,
} from '@mui/material';
import { useCallback } from 'react';
import { Typography } from '@mui/material';
import { FeeHurdleBasis } from 'further-types/investment';
import { GrossOrNetFee } from 'further-types/performance-fee';
import { PERFORMANCE_FEE_TEMPLATES } from 'adminConstants/routes';
import { useHistory } from 'react-router-dom';
import { Grid } from '@mui/material';
import { NumericFormat } from 'react-number-format';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import AppCheckbox from 'components/FormElements/AppCheckBox';

const ExcludeResidualCapitalFromHurdleContainer = styled('div')`
  margin-left: 10px;
`;

const AddEditPerformanceFeeTemplateForm: React.FC<{
  isLoading: boolean;
  isEdit?: boolean;
  templateInUse?: boolean;
}> = ({ isLoading, templateInUse, isEdit }) => {
  const { isSuperAdmin } = useGetRole();
  const [canCreatePerformanceFeeTemplate] = useHasPermissions([
    RolePermission.CreatePerformanceFees,
  ]);
  const { control, formState, setValue } = useFormContext();
  const firmId = useWatch({ control, name: 'firmId' });
  const performanceFees = useWatch({ control, name: 'performanceFees' });
  const hurdleBasis = useWatch({ control, name: 'hurdleBasis' });
  const grossOrNetFees = useWatch({ control, name: 'grossOrNetFees' });
  const history = useHistory();

  const inputDisabled = !canCreatePerformanceFeeTemplate || templateInUse;

  const { append } = useFieldArray({
    control,
    name: 'performanceFees',
  });

  const GrossFeeContainer = styled('div')`
    height: 29px;
    // align with other elements without border
    padding: 9px 12px 9px 0px;
    border-radius: 4px;
  `;

  const DISABLED_TOOLTIP_MESSAGE =
    templateInUse && canCreatePerformanceFeeTemplate
      ? 'It is not possible to edit a performance fee template that is already in use.'
      : undefined;

  const handleGrossFeeRecalculation = useCallback(
    (
      e: {
        target: {
          name: string;
          value: string | number;
        };
      },
      index: number,
    ) => {
      const updatedFees = recalculatePerformanceFee(
        [...performanceFees],
        e.target?.name?.split('.').pop() || '',
        e.target?.value,
        index,
      );
      setValue('performanceFees', updatedFees, {
        shouldValidate: true,
      });
    },
    [performanceFees, setValue],
  );

  const performanceFeeSettingColumns = [
    {
      label: 'Hurdle basis',
      key: 'hurdleBasis',
      sort: false,
      render: () => (
        <Tooltip title={DISABLED_TOOLTIP_MESSAGE}>
          <AppSelectBox
            required
            data={[
              {
                label: 'When company hits hurdle',
                value: FeeHurdleBasis.Company,
              },
              {
                label: 'When whole subscription hits hurdle',
                value: FeeHurdleBasis.Investment,
              },
            ]}
            name="hurdleBasis"
            valueKey="value"
            labelKey="label"
            variant="outlined"
            value={hurdleBasis}
            disabled={inputDisabled}
            onChange={(e) => setValue('hurdleBasis', e.target.value)}
          />
        </Tooltip>
      ),
    },
    {
      label: 'Fee treatment',
      key: 'grossOrNetFees',
      tooltipText: (
        <Spacing>
          <p>
            The hurdle can include or ignore fees. It works for the two hurdle
            basis options as follows:
          </p>
          <p>
            <strong>When company hits hurdle (deal-by-deal):</strong> With a
            1.0x hurdle, a £25,000 subscription and £1,000 charged fees pro-rata
            to the company subscription, the system will require £26,000 to be
            returned if <em>Subscription including fees</em> is selected, and
            £25,000 if <em>Subscription minus fees</em> is selected. A 2.0x
            hurdle will require £52,000 and £50,000 respectively.
          </p>
          <p>
            <strong>When whole subscription hits hurdle:</strong> With a 1.0x
            hurdle, a £25,000 subscription and £1,000 charged fees, the system
            will require £25,000 to be returned if{' '}
            <em>Subscription including fees</em>
            is selected, and £24,000 if <em>Subscription minus fees</em> is
            selected. A 2.0x hurdle will require £50,000 and £48,000
            respectively.
          </p>
        </Spacing>
      ),
      tooltipWide: true,
      sort: false,
      render: () => (
        <Tooltip title={DISABLED_TOOLTIP_MESSAGE}>
          <AppSelectBox
            required
            data={[
              {
                label: 'Subscription including fees',
                value: GrossOrNetFee.Net,
              },
              {
                label: 'Subscription minus fees',
                value: GrossOrNetFee.Gross,
              },
            ]}
            name="grossOrNetFees"
            valueKey="value"
            labelKey="label"
            variant="outlined"
            value={grossOrNetFees}
            disabled={inputDisabled}
            onChange={(e) => setValue('grossOrNetFees', e.target.value)}
          />
        </Tooltip>
      ),
    },
  ];

  const columns = [
    {
      label: 'Fee name',
      key: 'feeName',
      sort: false,
      render: (_, i) => (
        <Controller
          name={`performanceFees.${i}.feeName`}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Tooltip title={DISABLED_TOOLTIP_MESSAGE}>
              <AppTextInput
                {...field}
                placeholder="Enter fee name"
                disabled={inputDisabled}
                fullWidth
                inputRef={field.ref}
                error={!!error}
                helperText={error?.message}
                variant="outlined"
                style={{ minWidth: 200 }}
              />
            </Tooltip>
          )}
        />
      ),
    },
    {
      label: 'Hurdle (1.0 = 1.0x return)',
      key: 'hurdle',
      sort: false,
      render: (_, i) => (
        <Controller
          name={`performanceFees.${i}.hurdle`}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Tooltip title={DISABLED_TOOLTIP_MESSAGE}>
              <NumericFormat
                onValueChange={({ value }) =>
                  field.onChange(value ? Number(value) : '')
                }
                value={field.value}
                allowNegative={false}
                allowLeadingZeros={false}
                customInput={AppTextInput}
                error={!!error}
                helperText={error?.message}
                disabled={inputDisabled}
              />
            </Tooltip>
          )}
        />
      ),
    },
    {
      label: 'Fee (ex VAT)',
      key: 'feePercentageExVAT',
      sort: false,
      render: (_, i) => (
        <Controller
          name={`performanceFees.${i}.feePercentageExVAT`}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Tooltip title={DISABLED_TOOLTIP_MESSAGE}>
              <NumericFormat
                onValueChange={({ value }) => {
                  field.onChange(value ? Number(value) : '');
                  handleGrossFeeRecalculation(
                    {
                      target: {
                        name: `performanceFees.${i}.feePercentageExVAT`,
                        value: value ? Number(value) : '',
                      },
                    },
                    i,
                  );
                }}
                placeholder="e.g. 20"
                value={field.value}
                allowNegative={false}
                allowLeadingZeros={false}
                customInput={AppTextInput}
                error={!!error}
                helperText={error?.message}
                disabled={inputDisabled}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="start">%</InputAdornment>
                  ),
                }}
              />
            </Tooltip>
          )}
        />
      ),
    },
    {
      label: 'VAT',
      key: 'vatPercentage',
      sort: false,
      render: (_, i: number) => (
        <Controller
          name={`performanceFees.${i}.vatPercentage`}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Tooltip title={DISABLED_TOOLTIP_MESSAGE}>
              <AppSelectBox
                {...field}
                disabled={inputDisabled}
                onChange={(e: SelectChangeEvent<unknown>) => {
                  field.onChange(
                    e.target.value === '' ? '' : Number(e.target.value),
                  );
                  handleGrossFeeRecalculation(
                    {
                      target: {
                        name: `performanceFees.${i}.vatPercentage`,
                        value: e.target.value as number,
                      },
                    },
                    i,
                  );
                }}
                data={[
                  { label: '20% VAT', value: 20 },
                  { label: 'No VAT', value: 0 },
                ]}
                valueKey="value"
                labelKey="label"
                variant="outlined"
                error={!!error}
                helperText={error?.message}
                style={{ minWidth: 80 }}
                required
              />
            </Tooltip>
          )}
        />
      ),
    },
    {
      label: 'Gross fee',
      key: 'feePercentageIncVAT',
      sort: false,
      render: (elm) => {
        return (
          <GrossFeeContainer>
            {`${Intl.NumberFormat('en-GB', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
            }).format(elm.feePercentageIncVAT)}%`}
          </GrossFeeContainer>
        );
      },
    },
    {
      label: 'Action',
      key: 'action',
      sort: false,
      render: (_, i: number) => (
        <Button
          size="small"
          disabled={inputDisabled}
          tooltip={
            templateInUse && canCreatePerformanceFeeTemplate
              ? 'It is not possible to delete a performance fee template that is currently in use.'
              : undefined
          }
          onClick={() => {
            const updatedPerformanceFees = performanceFees.filter(
              (_, index) => index !== i,
            );
            setValue('performanceFees', updatedPerformanceFees);
          }}
        >
          <Delete color={inputDisabled ? 'disabled' : 'primary'} />
        </Button>
      ),
    },
  ];

  // Check if the form has any errors or if there are no performance fees
  const isFormInvalid =
    Object.keys(formState.errors).length > 0 || !performanceFees?.length;

  return (
    <Spacing>
      <Grid container>
        <Grid size={{ md: 6, xs: 12 }}>
          <Spacing>
            {isSuperAdmin && (
              <FieldRow centerTitle title="Firm" narrow>
                <FirmSelector
                  firmId={firmId}
                  disabled={inputDisabled}
                  onChange={(firmId) => {
                    setValue('firmId', firmId, { shouldValidate: true });
                  }}
                  required={true}
                />
              </FieldRow>
            )}

            <FieldRow title="Fee template name" centerTitle narrow>
              <Controller
                name="feeTemplateName"
                control={control}
                disabled={!canCreatePerformanceFeeTemplate}
                render={({ field, fieldState: { error } }) => (
                  <AppTextInput
                    {...field}
                    onChange={(e) => {
                      setValue('feeTemplateName', e.target.value, {
                        shouldDirty: false,
                        shouldValidate: false,
                      });
                    }}
                    onBlur={(e) => {
                      setValue('feeTemplateName', e.target.value, {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                    }}
                    error={!!error}
                    helperText={error?.message}
                    placeholder="e.g. 10% performance fee at a hurdle of 1.1"
                  />
                )}
              />
            </FieldRow>
          </Spacing>
        </Grid>
      </Grid>

      <Typography variant="h6">Performance fees</Typography>
      <p>
        These fees are charged on exits. If you have multiple performance fee
        tiers (based on hurdle rate), add each one and the full fee (e.g. 2x
        20%, 5x 25%, etc). Larger hurdle fees override smaller hurdle fees from
        the larger hurdle point.
      </p>

      <Spacing size="sm">
        <Table
          columns={performanceFeeSettingColumns}
          tablebody={[{}]}
          variant="nohover"
          title="Apply these settings to all performance fees:"
          hideEmptyMessage
          smallTitle
          hideFooterUnderline
        />

        {hurdleBasis === FeeHurdleBasis.Investment && (
          <Controller
            name="excludeResidualCapitalFromHurdle"
            control={control}
            render={({ field }) => (
              <Tooltip title={DISABLED_TOOLTIP_MESSAGE}>
                <ExcludeResidualCapitalFromHurdleContainer>
                  <AppCheckbox
                    label="Exclude residual capital from hurdle calculation"
                    checked={field.value ?? false}
                    disabled={inputDisabled}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                </ExcludeResidualCapitalFromHurdleContainer>
              </Tooltip>
            )}
          />
        )}
      </Spacing>

      <Table
        columns={columns}
        tablebody={performanceFees}
        variant="nohover"
        key={performanceFees.length}
        alignRowsTop
      />

      <ButtonGroup>
        <Button
          onClick={() => {
            append({
              feeName: '',
              hurdle: '',
              feePercentageExVAT: '',
              feePercentageIncVAT: '',
              vatPercentage: 0,
            });
          }}
          color={templateInUse ? 'secondary' : 'primary'}
          variant="text"
          disabled={templateInUse}
        >
          <strong>Add fee</strong>
        </Button>
      </ButtonGroup>
      <ButtonGroup>
        {isEdit && (
          <Button
            type="button"
            color="primary"
            variant="outlined"
            onClick={() => history.push(PERFORMANCE_FEE_TEMPLATES)}
          >
            Cancel
          </Button>
        )}
        {canCreatePerformanceFeeTemplate && (
          <Button
            type="submit"
            color="primary"
            variant="contained"
            disabled={isFormInvalid || !formState.isDirty}
            loading={isLoading}
          >
            {isEdit ? 'Update fee template' : 'Save fee template'}
          </Button>
        )}
      </ButtonGroup>
    </Spacing>
  );
};

export default AddEditPerformanceFeeTemplateForm;
